import { createApp, h, watch } from 'vue';
import { createInertiaApp, Head, Link, router, usePage } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from 'ziggy-js';
import '@fontsource-variable/inter';
import '../css/app.css';
import './composables/theme-init.js';
import './bootstrap.js';
import Toast from '@/Components/UI/Toast.vue';
import PageLoader from '@/Components/UI/PageLoader.vue';
import EmptyState from '@/Components/UI/EmptyState.vue';
import LoadingButton from '@/Components/UI/LoadingButton.vue';
import DialogHost from '@/Components/UI/DialogHost.vue';
import dialogs from '@/Plugins/dialogs.js';
import clickOutside from '@/Plugins/clickOutside.js';
import ThemeToggle from '@/Components/UI/ThemeToggle.vue';
import { useOneSignal } from '@/composables/useOneSignal.js';

/**
 * Helper publico: define si el contexto actual debe ofrecer PWA.
 * Lo consulta PwaInstallPrompt.vue para no duplicar la lista de paths.
 * Cualquier ruta no listada como portal autenticado se considera publica.
 */
/**
 * POR QUE EXISTE UN MANEJADOR GLOBAL DE ERRORES
 *
 * Inertia devuelve los errores de validacion en `props.errors` y, si la
 * pagina no los muestra, **no pasa nada visible**: el boton deja de girar y
 * el formulario aparece vacio otra vez. El usuario no sabe si fallo, si se
 * guardo, ni que campo esta mal.
 *
 * Cuando se reviso el arbol, de las pantallas que escriben con
 * `router.post/put/...`, 55 no tenian ni `onError`, ni prop `errors`, ni
 * aviso. Cincuenta y cinco formularios que fallan en silencio.
 *
 * POR QUE UN MANEJADOR Y NO CINCUENTA Y CINCO ARREGLOS
 *
 * Porque todas las peticiones de Inertia pasan por aqui. Es el unico punto
 * por el que sale toda escritura de la aplicacion, asi que un manejador aqui
 * cubre las 55 y cubre las que se anadan manana, sin que nadie tenga que
 * acordarse.
 *
 * Y porque es la opuesta de integrar antes de crear en su otra version: no
 * se crea un componente, se usa el canal `nido:toast` que `Toast.vue` ya
 * escucha desde hace tiempo.
 *
 * POR QUE UN EVENTO Y NO `onError` EN `createInertiaApp`
 *
 * Porque en Inertia 3.1.1 esa opcion NO EXISTE. `createInertiaApp` acepta
 * `id, resolve, setup, title, progress, page, render, defaults, nonce, http,
 * layout, withApp`; un `onError` ahi se ignora en silencio y parece que
 * funciona. Se perdio una corrida entera por eso.
 *
 * Lo que si dispara Inertia 3 es un `CustomEvent` `inertia:error` sobre el
 * `document`, con `{ detail: { errors } }`, ANTES de llamar al `onError` de
 * la visita. Eso es lo que se escucha aqui.
 *
 * Y como el evento se emite siempre, la unica forma de que una pantalla diga
 * «esto ya lo aviso yo» es no montar el `<Toast />` en su layout. Que es
 * exactamente lo que hace `GuestLayout`: `/login` y `/register` pintan el
 * error bajo el campo, y alli un aviso flotante diria lo mismo dos veces.
 * La visibilidad la decide el layout, no una lista de paginas.
 */
window.__nidoAvisarErrorEnGuardado = function (errors) {
    if (typeof window === 'undefined') return;

    const campos = Object.entries(errors || {})
        .flatMap(([, mensajes]) => (Array.isArray(mensajes) ? mensajes : [mensajes]))
        .filter(Boolean);

    if (!campos.length) return;

    // Con muchos campos a la vez, listarlos todos es ruido: el texto se
    // corta y nadie lo lee entero. El detalle ya esta en la pagina.
    const texto = campos.length > 3
        ? `${campos.slice(0, 3).join(' · ')} y ${campos.length - 3} más`
        : campos.join(' · ');

    window.dispatchEvent(new CustomEvent('nido:toast', {
        detail: { type: 'error', message: texto },
    }));
};

document.addEventListener('inertia:error', (evento) => {
    window.__nidoAvisarErrorEnGuardado(evento?.detail?.errors);
});

window.__nidoPwaAllowedPaths = ['/portal', '/direccion', '/staff-portal', '/admin'];
window.__nidoShouldOfferPwa = function() {
    const path = window.location.pathname;
    if (path === '/' || path === '/login' || path === '/register') return false;
    if (path.startsWith('/food-service/')) return false;
    return window.__nidoPwaAllowedPaths.some(p => path === p || path.startsWith(p + '/'));
};

/**
 * Inyecta dinamicamente <link rel="manifest"> cuando se navega a un portal
 * desde Inertia SPA. Si la pagina inicial fue /login, Blade no emitio el
 * manifest para evitar prompts publicos; al entrar al portal tras el login,
 * este helper inyecta el manifest correcto segun la ruta/rol.
 */
function ensurePwaManifest() {
    if (typeof window === 'undefined' || !document.head) return;
    if (!window.__nidoShouldOfferPwa || !window.__nidoShouldOfferPwa()) return;

    const path = window.location.pathname;
    let href = '/manifest.json';
    if (path.startsWith('/portal/aula')) {
        href = '/manifest-teacher.json';
    } else if (path.startsWith('/portal')) {
        href = '/manifest-family.json';
    }

    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
        link = document.createElement('link');
        link.rel = 'manifest';
        link.crossOrigin = 'use-credentials';
        document.head.appendChild(link);
    }
    if (link.getAttribute('href') !== href) {
        link.setAttribute('href', href);
    }
}

router.on('navigate', () => {
    ensurePwaManifest();
});

/**
 * Registra /sw.js como unico Service Worker controlando scope '/'.
 *
 * Se llama ANTES de inicializar OneSignal para que el SW este activo
 * cuando OneSignal intente detectar si ya existe un SW controlando
 * el scope raiz (modo "Custom Code" de OneSignal v16).
 *
 * Si el registro falla (modo incognito, HTTP sin SW, etc.) la app sigue
 * funcionando: solo se pierde el precache y los push, no el render.
 */
function registerServiceWorker() {
    if (! ('serviceWorker' in navigator)) return;

    const buildId = (typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : Date.now().toString());

    // Esperar a que la pagina termine de cargar antes de registrar evita
    // contention con el primer render y mejora TTFB.
    window.addEventListener('load', () => {
        navigator.serviceWorker.register(`/sw.js?v=${buildId}`, {
            scope: '/',
            updateViaCache: 'none',
        }).catch((err) => {
            console.warn('[SW] registro fallo:', err?.message);
        });
    });
}

// En rutas publicas (login, register, welcome, food-service) no queremos que
// el navegador muestre su banner nativo de instalar app. El manifest sigue
// servirse globalmente para que Inertia pueda inyectarlo en la siguiente
// navegacion sin recargar, y el SW se registra siempre (notificaciones y
// offline dependen de el). Solo suprimimos el prompt nativo fuera de los
// portales autenticados.
window.addEventListener('beforeinstallprompt', (event) => {
    if (window.__nidoShouldOfferPwa && !window.__nidoShouldOfferPwa()) {
        event.preventDefault();
    }
}, { capture: true });

window.addEventListener('vite:preloadError', (event) => {
    window.location.reload();
});

registerServiceWorker();

createInertiaApp({
    resolve: name => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        // Componente "fantasma" que se encarga de inicializar OneSignal.
        // Necesita ser un componente real de Vue para que usePage()
        // (que usa inject() internamente) funcione correctamente.
        const OneSignalManager = {
            setup() {
                const page = usePage();
                watch(() => page.props?.auth?.user, (user) => {
                    if (user) {
                        useOneSignal().init({ user }).catch((err) => {
                            console.warn('[OneSignal] init fallo:', err?.message);
                        });
                    }
                }, { immediate: true });
                return () => null;
            }
        };

        const app = createApp({
            render: () => h('div', [
                h(OneSignalManager),
                h(App, props),
            ])
        });
        app.use(plugin);
        app.use(ZiggyVue);
        app.use(dialogs);
        app.use(clickOutside);
        app.component('Head', Head);
        app.component('Link', Link);
        app.component('Toast', Toast);
        app.component('PageLoader', PageLoader);
        app.component('EmptyState', EmptyState);
        app.component('LoadingButton', LoadingButton);
        app.component('DialogHost', DialogHost);
        app.component('ThemeToggle', ThemeToggle);
        app.mount(el);
    },
    progress: { delay: 250, color: '#f97316' },
});
